import React, { useState } from 'react';

const CINVerificationDashboard = ({ extractedData, onVerify }) => {
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedCin, setVerifiedCin] = useState('');

  const handleVerify = async () => {
    const currentCin = extractedData?.cin;
    if (isVerifying || !currentCin || verifiedCin === currentCin) return;
    
    setIsVerifying(true);
    try {
      const result = await onVerify(extractedData);
      setVerificationResult(result);
      setVerifiedCin(currentCin);
    } catch (error) {
      setVerificationResult({
        status: 'error',
        message: 'Verification failed'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'match':
        return <span className="text-green-500">✅</span>;
      case 'partial':
        return <span className="text-yellow-500">⚠️</span>;
      case 'mismatch':
        return <span className="text-red-500">❌</span>;
      default:
        return <span className="text-gray-500">-</span>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'match':
        return 'bg-green-50 border-green-200';
      case 'partial':
        return 'bg-yellow-50 border-yellow-200';
      case 'mismatch':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (!extractedData?.cin) {
    return null;
  }

  if (extractedData.cin !== verifiedCin && verificationResult) {
    setVerificationResult(null);
    setVerifiedCin('');
  }

  const isAlreadyVerified = verifiedCin === extractedData.cin && verificationResult;

  return (
    <div className="mt-6 p-4 border rounded-lg bg-blue-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-blue-800">CIN Verification</h3>
        <button
          onClick={handleVerify}
          disabled={isVerifying || isAlreadyVerified}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isVerifying ? 'Verifying...' : isAlreadyVerified ? 'Verified' : 'Verify with Official API'}
        </button>
      </div>

      {verificationResult && (
        <div className="space-y-4">
          {verificationResult.status === 'error' ? (
            <div className="p-3 bg-red-100 border border-red-300 rounded text-red-700">
              {verificationResult.message}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                {Object.entries(verificationResult.verification_results || {}).map(([field, result]) => (
                  <div key={field} className={`p-3 border rounded ${getStatusColor(result.status)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{field.replace('_', ' ')}</span>
                      {getStatusIcon(result.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Extracted:</span>
                        <div className="font-mono">{result.extracted || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Official API:</span>
                        <div className="font-mono">{result.api || 'N/A'}</div>
                      </div>
                    </div>
                    {result.similarity && (
                      <div className="mt-1 text-xs text-gray-600">
                        Similarity: {(result.similarity * 100).toFixed(1)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-gray-100 border rounded">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Overall Verification Score</span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    verificationResult.overall_match?.status === 'verified' 
                      ? 'bg-green-100 text-green-800'
                      : verificationResult.overall_match?.status === 'partial'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {verificationResult.overall_match?.score}% 
                    ({verificationResult.overall_match?.status})
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {verificationResult.overall_match?.matched_fields} of {verificationResult.overall_match?.total_fields} fields matched
                </div>
              </div>

              {verificationResult.api_data?.details && (
                <div className="mt-4 bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Additional Details from API</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {verificationResult.api_data.company_type && (
                      <div className="text-xs">
                        <span className="text-gray-600">Company Type:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.company_type}</div>
                      </div>
                    )}
                    {verificationResult.api_data.details.company_info?.roc_code && (
                      <div className="text-xs">
                        <span className="text-gray-600">ROC Code:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.details.company_info.roc_code}</div>
                      </div>
                    )}
                    {verificationResult.api_data.details.company_info?.company_status && (
                      <div className="text-xs">
                        <span className="text-gray-600">Company Status:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.details.company_info.company_status}</div>
                      </div>
                    )}
                    {verificationResult.api_data.details.company_info?.authorized_capital && (
                      <div className="text-xs">
                        <span className="text-gray-600">Authorized Capital:</span>
                        <div className="font-medium text-blue-900 mt-1">₹{parseInt(verificationResult.api_data.details.company_info.authorized_capital).toLocaleString()}</div>
                      </div>
                    )}
                    {verificationResult.api_data.details.company_info?.paid_up_capital && (
                      <div className="text-xs">
                        <span className="text-gray-600">Paid-up Capital:</span>
                        <div className="font-medium text-blue-900 mt-1">₹{parseInt(verificationResult.api_data.details.company_info.paid_up_capital).toLocaleString()}</div>
                      </div>
                    )}
                    {verificationResult.api_data.details.company_info?.email_id && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Email:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.details.company_info.email_id}</div>
                      </div>
                    )}
                    {verificationResult.api_data.details.directors && verificationResult.api_data.details.directors.length > 0 && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Directors ({verificationResult.api_data.details.directors.length}):</span>
                        <div className="mt-1 space-y-1">
                          {verificationResult.api_data.details.directors.slice(0, 5).map((director, idx) => (
                            <div key={idx} className="font-medium text-blue-900">
                              • {director.director_name} (DIN: {director.din_number})
                            </div>
                          ))}
                          {verificationResult.api_data.details.directors.length > 5 && (
                            <div className="text-gray-500">... and {verificationResult.api_data.details.directors.length - 5} more</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CINVerificationDashboard;
